import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Components
import SubmitButton from '@atoms/SubmitButton';
import DragDropFile from '@molecules/DragDropFile';
import ImageEditor from '@molecules/ImageEditor';
import { ErrorText, Instructions } from '@styled';

// Functionality
import { GlobalModalContext } from '@context/globalModalContext';
import useCompanyLogo from '@hooks/useCompanyLogo';
import useImageCropper from '@hooks/useImageCropper';

export const useCompanyEditLogoModal = () => {
    const [, setIsModalOpen, setModalChildren, setModalOuterClass] = useContext(GlobalModalContext);

    useEffect(() => {
        setModalOuterClass('company-edit-logo-modal');
    }, [setModalOuterClass]);

    const openModal = ({ currentImageSrc }) =>
        new Promise(resolve => {
            setModalChildren(() => (
                <CompanyEditLogoModal
                    currentImageSrc={currentImageSrc}
                    onLogoUpdate={({ logoUrl }) => {
                        resolve({ logoUrl });
                    }}
                />
            ));

            setIsModalOpen(true);
        });

    return { openModal };
};

const CompanyEditLogoModal = ({ currentImageSrc, onLogoUpdate }) => {
    const [, setIsModalOpen] = useContext(GlobalModalContext);

    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const [imageSrc, setImageSrc] = useState(currentImageSrc);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null); // e.g. {height: 3024, width: 3024, x: 0, y: 504}

    const { getCropped } = useImageCropper();
    const { updateCompanyLogo } = useCompanyLogo();

    const onDrop = async acceptedFiles => {
        if (acceptedFiles.length === 0) {
            return;
        }

        setImageSrc(URL.createObjectURL(acceptedFiles[0]));
    };

    const handleSubmit = async () => {
        setSubmitting(true);

        try {
            const blob = await getCropped({
                file: imageSrc,
                croppedAreaPixels,
                finalHeight: 200,
                finalWidth: 200,
            });

            const croppedFile = new File([blob], `file-${Date.now()}.jpeg`, {
                type: 'image/jpeg',
                lastModified: new Date(),
            });

            const { url, uploadUrl } = await updateCompanyLogo();
            await fetch(uploadUrl, { method: 'PUT', body: croppedFile });
            setSuccess(true);
            onLogoUpdate({ logoUrl: url });

            setTimeout(() => {
                setIsModalOpen(false);
            }, 1000);
        } catch (e) {
            setError(e);
        }

        setSubmitting(false);
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixelsOnComplete) => {
        setCroppedAreaPixels(croppedAreaPixelsOnComplete);
    }, []);

    return (
        <div>
            <h3>Add a Company Logo</h3>
            <Instructions>Add a company logo to help fellow members identify your brand.</Instructions>
            <div className="row flex-column-reverse flex-md-row">
                <div className="col-md-6">
                    <ImageEditor imageSrc={imageSrc} onCropComplete={onCropComplete} />
                </div>
                <div className="col-md-6 mb-4 mb-md-0">
                    <DragDropFile onDrop={onDrop} isLoading={submitting} />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    {error && (
                        <div className="mt-3">
                            <ErrorText>There was an error uploading your image. Try another file.</ErrorText>
                        </div>
                    )}

                    <SubmitButton
                        onClick={handleSubmit}
                        isSubmitting={submitting}
                        isError={error}
                        isSuccessful={success}
                    >
                        Save Profile Image
                    </SubmitButton>
                </div>
            </div>
        </div>
    );
};

CompanyEditLogoModal.propTypes = {
    currentImageSrc: PropTypes.string,
    onLogoUpdate: PropTypes.func,
};

export default CompanyEditLogoModal;
